/**
 * MarshmallowRenderer — layered browning gradient marshmallow for Phaser 3.
 *
 * ═══════════════════════════════ Public API ═══════════════════════════════
 *
 *   import { MarshmallowRenderer } from '../render/marshmallow';
 *
 *   // In PlayScene.create():
 *   private marshmallow!: MarshmallowRenderer;
 *   this.marshmallow = new MarshmallowRenderer(this);
 *
 *   // In PlayScene.update():
 *   this.marshmallow.update(x, y, toastedness, heat, burnt, this.time.now);
 *
 *   // In scene shutdown / destroy:
 *   this.marshmallow.destroy();
 *
 * Parameters passed to update():
 *   x, y         World position — centre of the marshmallow.
 *   toastedness  0..100 — drives the browning gradient white→golden→brown→char.
 *   heat         0..100 — drives the heat-glow aura (warm-gold in-band, red danger).
 *   burnt        true   — snaps to char appearance; suppresses jiggle animation.
 *   time         scene.time.now (ms) — drives jiggle and char-spot flicker.
 *
 * Depth: 3 (matches the PlayScene stack: flame=1, stick=2, marshmallow=3).
 * ════════════════════════════════════════════════════════════════════════════
 */

import Phaser from 'phaser';
import { BURN_THRESHOLD } from '../sim/constants';

// Heat at which the warm aura first appears (just below the golden band).
const GLOW_START = 50;
// Upper fraction of the GLOW_START→BURN_THRESHOLD ramp that reads as alarm-red,
// so the danger cue saturates to full red exactly at the burn point.
const DANGER_FRAC = 0.7;
const DANGER_START = GLOW_START + (BURN_THRESHOLD - GLOW_START) * DANGER_FRAC;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

// ── Colour palette ─────────────────────────────────────────────────────────

const C_RAW   = 0xfff7e8; // raw, un-toasted: warm white
const C_WARM  = 0xffe0a8; // warming up
const C_GOLD  = 0xffc46b; // perfectly golden — the player's visual target
const C_TOAST = 0xd4822a; // deeply toasted
const C_BROWN = 0x8c4a16; // well-done, dark brown
const C_CHAR  = 0x1c1008; // charred black

// ── Helpers ────────────────────────────────────────────────────────────────

/** Linear-interpolate between two packed 0xRRGGBB colours. t clamped 0..1. */
function lerpHex(a: number, b: number, t: number): number {
  const tc = t < 0 ? 0 : t > 1 ? 1 : t;
  const r  = ((a >> 16 & 0xff) + ((b >> 16 & 0xff) - (a >> 16 & 0xff)) * tc) | 0;
  const gv = ((a >> 8  & 0xff) + ((b >> 8  & 0xff) - (a >> 8  & 0xff)) * tc) | 0;
  const bv = ((a       & 0xff) + ((b       & 0xff) - (a       & 0xff)) * tc) | 0;
  return (r << 16) | (gv << 8) | bv;
}

/** Toastedness (0..100) + burnt flag → packed body fill colour. */
function bodyFill(toastedness: number, burnt: boolean): number {
  if (burnt) return C_CHAR;
  const t = toastedness < 0 ? 0 : toastedness > 100 ? 1 : toastedness / 100;
  if (t < 0.30) return lerpHex(C_RAW,   C_WARM,  t / 0.30);
  if (t < 0.60) return lerpHex(C_WARM,  C_GOLD,  (t - 0.30) / 0.30);
  if (t < 0.80) return lerpHex(C_GOLD,  C_TOAST, (t - 0.60) / 0.20);
  return              lerpHex(C_TOAST, C_BROWN, (t - 0.80) / 0.20);
}

/** Rim fill: darkened version of the body colour for a moulded-edge look. */
function rimFill(toastedness: number, burnt: boolean): number {
  const base = bodyFill(toastedness, burnt);
  const r  = Math.max(0, (base >> 16 & 0xff) - 44);
  const gv = Math.max(0, (base >> 8  & 0xff) - 32);
  const bv = Math.max(0, (base       & 0xff) - 18);
  return (r << 16) | (gv << 8) | bv;
}

/**
 * Pre-set char-spot positions relative to marshmallow centre.
 * Fixed offsets keep the pattern visually consistent and avoid Math.random.
 * Tuple: [relX, relY, radius]
 */
const CHAR_SPOTS: ReadonlyArray<readonly [number, number, number]> = [
  [-12, -20, 6],
  [ 15,  -5, 5],
  [  3,  19, 7],
  [-18,   9, 4],
  [ 19,  17, 3],
];

// ── Renderer ───────────────────────────────────────────────────────────────

export class MarshmallowRenderer {
  /** Nominal body width in pixels — useful for PlayScene layout maths. */
  readonly bodyW = 64;
  /** Nominal body height in pixels — useful for PlayScene layout maths. */
  readonly bodyH = 80;

  private readonly g: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene) {
    this.g = scene.add.graphics().setDepth(3);
  }

  /**
   * Redraw every frame. Call from PlayScene.update() before the sim step or
   * after — whichever ordering your scene prefers.
   *
   * @param x           Centre X in world / scene coordinates.
   * @param y           Centre Y in world / scene coordinates.
   * @param toastedness 0..100 browning progress from the sim.
   * @param heat        0..100 current heat value from the sim.
   * @param burnt       true → snap to charred appearance.
   * @param time        scene.time.now in milliseconds.
   */
  update(
    x: number,
    y: number,
    toastedness: number,
    heat: number,
    burnt: boolean,
    time: number,
  ): void {
    const g = this.g;
    g.clear();
    g.setPosition(x, y);

    // Gentle Y-axis jiggle (breathing animation). Suppressed when burnt.
    const scaleY = 1 + (!burnt ? Math.sin(time * 0.0028) * 0.024 : 0);
    const W = this.bodyW;
    const H = this.bodyH * scaleY;

    // ── Heat-glow aura ─────────────────────────────────────────────────────
    // Appears when heat enters the toasting zone and ramps to a full-red alarm
    // exactly at the burn point. The ramp is derived from BURN_THRESHOLD so it
    // stays calibrated if the balance pass retunes the threshold (the player
    // MUST see escalating red before burning).
    if (heat > GLOW_START) {
      const hNorm = clamp01((heat - GLOW_START) / (BURN_THRESHOLD - GLOW_START)); // 0 at GLOW_START → 1 at burn
      const glowHex =
        heat > DANGER_START
          ? // Danger zone: orange → full red, saturating at BURN_THRESHOLD.
            lerpHex(0xff8c20, 0xff2200, clamp01((heat - DANGER_START) / (BURN_THRESHOLD - DANGER_START)))
          : // Toasting zone: warm gold → orange.
            lerpHex(0xffd27a, 0xff8c20, clamp01((heat - GLOW_START) / (DANGER_START - GLOW_START)));
      const glowA = 0.06 + hNorm * 0.12;
      const pulse = 1 + Math.sin(time * 0.0046) * 0.04 * hNorm;
      g.fillStyle(glowHex, glowA);
      g.fillEllipse(0, 0, (W + 24) * pulse, (H + 30) * pulse);
    }

    // ── Drop shadow ────────────────────────────────────────────────────────
    g.fillStyle(0x000000, 0.16);
    g.fillEllipse(5, H * 0.53, W * 0.82, 18);

    // ── Outer rim (slightly darker moulded edge) ───────────────────────────
    g.fillStyle(rimFill(toastedness, burnt), 1.0);
    g.fillEllipse(0, 0, W + 4, H + 4);

    // ── Main body ──────────────────────────────────────────────────────────
    g.fillStyle(bodyFill(toastedness, burnt), 1.0);
    g.fillEllipse(0, 0, W, H);

    if (!burnt) {
      // ── 3-D roundness illusion: specular highlights + depth shadow ──────
      // Diffuse specular — upper-left quadrant
      g.fillStyle(0xffffff, 0.18);
      g.fillEllipse(-W * 0.13, -H * 0.21, W * 0.42, H * 0.36);
      // Sharp specular dot
      g.fillStyle(0xffffff, 0.28);
      g.fillEllipse(-W * 0.19, -H * 0.29, W * 0.17, H * 0.13);
      // Lower-right depth shadow band
      g.fillStyle(0x000000, 0.06);
      g.fillEllipse(W * 0.09, H * 0.11, W * 0.54, H * 0.54);

      // ── Equator crust band (toasting browning line) ─────────────────────
      // Fades in from toastedness ≈44 to ≈86 — visible cue that it's working.
      if (toastedness > 44) {
        const crustA = Math.min(1, (toastedness - 44) / 42) * 0.26;
        g.fillStyle(rimFill(100, false), crustA);
        g.fillEllipse(0, H * 0.02, W * 0.90, H * 0.11);
      }
    }

    // ── Char spots ─────────────────────────────────────────────────────────
    // Appear progressively from toastedness ≥74 and reach full coverage on burn.
    if (burnt || toastedness >= 74) {
      const progress = burnt ? 1 : Math.min(1, (toastedness - 74) / 26);
      const charA    = 0.10 + progress * 0.72 + Math.sin(time * 0.004) * 0.03 * progress;
      const count    = burnt
        ? CHAR_SPOTS.length
        : Math.max(1, Math.ceil(CHAR_SPOTS.length * progress));

      for (let i = 0; i < count; i++) {
        const [sx, sy, sr] = CHAR_SPOTS[i]!;
        g.fillStyle(C_CHAR, Math.min(0.95, charA));
        g.fillEllipse(sx, sy * scaleY, sr * 2.0, sr * 1.5);
        // Inner texture highlight on the char scar
        if (burnt) {
          g.fillStyle(0x3a1c08, 0.45);
          g.fillEllipse(sx + 1, sy * scaleY - 1, sr, sr * 0.7);
        }
      }
    }
  }

  /** Release the Phaser Graphics object. Call on scene shutdown. */
  destroy(): void {
    this.g.destroy();
  }
}
